<template>
  <v-card
    v-if="loading"
    height="150px"
    class="rounded-lg d-flex justify-center align-center"
    flat
  >
    <div>
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
  </v-card>
  <v-card v-else class="rounded-lg pa-4" flat>
    <v-card-title class="justify-center">
      {{ title }}
    </v-card-title>
    <v-card-text class="pa-4 text-center">
      <span class="subtitle-1 grey--text text--darken-3">
        {{ description }}
      </span>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-text class="mt-4">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row>
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <h3 class="body-1 text-center">
              {{ $t('organization_billing.create.pricing_model') }}
            </h3>
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="organizationPlanCreate.pricing_model_id"
              :items="pricingModels"
              item-text="display_name"
              item-value="id"
              :rules="rules.pricingModelRules"
              hide-details
              outlined
              @change="updatePricingModel"
            >
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="organizationPlanCreate.pricing_model_id">
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <h3 class="body-1 text-center">
              {{ $t('organization_billing.create.currency') }}
            </h3>
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="organizationPlanCreate.currency_id"
              :items="currenciesPlans"
              item-text="name"
              item-value="id"
              :rules="rules.currencyRules"
              hide-details
              outlined
              @change="updateCurrency"
            >
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="organizationPlanCreate.currency_id">
          <v-col cols="12" md="6" class="d-flex justify-center align-center">
            <h3 class="body-1 text-center">
              {{ $t('organization_billing.create.license') }}
            </h3>
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="organizationPlanCreate.plan_id"
              :items="filteredPlans"
              item-text="display_name"
              item-value="id"
              :rules="rules.planRules"
              hide-details
              outlined
              @change="updatePlan"
            >
            </v-autocomplete>
          </v-col>
        </v-row>
        <!------------------------------------------>
        <!-- SERVICES ------------------------------>
        <!------------------------------------------>
        <div v-if="showServicesAndResume">
          <v-row
            v-if="organizationPlanCreate.plan_id && planHasCustomAutomation()"
          >
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <h3 class="body-1 text-center">
                {{ $t('organization_billing.create.services') }}
              </h3>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="organizationPlanCreate.services"
                :items="filteredServices"
                item-text="display_name"
                item-value="id"
                :rules="rules.servicesRules"
                multiple
                chips
                hide-details
                outlined
                return-object
                @change="updateServices"
              >
                <template v-slot:item="{ props, item }">
                  <div>
                    <v-list-item
                      v-bind="props"
                      :disabled="isServiceTypeSelected(item)"
                    >
                      <v-list-item-title>
                        {{ item.display_name }}
                      </v-list-item-title>
                    </v-list-item>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <div
            v-if="organizationPlanCreate.plan_id && planHasServicesRelated()"
          >
            <v-row
              v-for="service in defaultRelatedServices"
              :key="service.id"
              class="mt-2"
            >
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center align-center"
              >
                <h3 class="body-1 text-center">
                  {{ $t('organization_billing.service') }}
                </h3>
              </v-col>
              <v-col cols="12" md="6">
                <v-card
                  class="rounded-md pa-4"
                  flat
                  outlined
                  style="border: 1px solid #9e9e9e;"
                >
                  <h3 class="body-1">{{ service.display_name }}</h3>
                </v-card>
              </v-col>
            </v-row>
          </div>
          <v-row v-if="organizationPlanCreate.plan_id && planHasCustomAmount()">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <h3 class="body-1 text-center">
                {{ $t('organization_billing.create.amount') }}
              </h3>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="organizationPlanCreate.amount"
                type="number"
                :placeholder="`Monto ${currencySelected}`"
                :label="currencySelected"
                :rules="rules.amountRules"
                outlined
                hide-details
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="organizationPlanCreate.plan_id && planHasCustomFee()">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <h3 class="body-1 pt-2 text-center">
                {{ $t('organization_billing.create.fee_percentage') }} <br />
                <p class="body-2 text-center">
                  {{ $t('organization_billing.create.fee_percentage_subtext') }}
                </p>
              </h3>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="organizationPlanCreate.fee_percentage"
                type="number"
                placeholder="Porcentaje (%)"
                :rules="rules.percentageRules"
                outlined
                hide-details
              >
              </v-text-field>
            </v-col>
          </v-row>
        </div>
      </v-form>

      <OrganizationPlanSummary
        v-if="displaySummary"
        :title="'Resumen del plan'"
        :subtitle="'Resumen de los servicios'"
        :data="formattedPlanSummary"
      />
    </v-card-text>
    <v-card-actions class="px-6 py-6">
      <v-spacer />
      <v-btn
        class="rounded-lg mr-4"
        outlined
        color="#2661FF"
        min-width="100px"
        @click="$emit('close')"
      >
        {{ cancelText }}
      </v-btn>
      <v-btn
        color="#2661FF"
        class="rounded-lg"
        min-width="100px"
        @click="createOrganizationPlan"
        :disabled="!valid"
      >
        <span style="color: #fff;">
          {{ acceptText }}
        </span>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import OrganizationPlanSummary from './OrganizationPlanSummary.vue'
import plansMixin from '@/mixins/plans'

export default {
  name: 'CreateOrganizationPlan',
  components: { OrganizationPlanSummary },
  mixins: [plansMixin],
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    cancelText: {
      type: String,
      required: true
    },
    acceptText: {
      type: String,
      required: true
    }
  },
  data: () => ({
    dialog: true,
    loading: false,
    valid: true,
    currencySelected: null,
    planDetail: null,
    servicesDetails: [],
    plansCustom: [],
    organizationPlanCreate: {
      plan_id: null,
      amount: null,
      fee_percentage: null,
      currency_id: null,
      pricing_model_id: null,
      services: []
    },
    rules: {
      pricingModelRules: [v => !!v || 'This field is required'],
      currencyRules: [v => !!v || 'This field is required'],
      planRules: [v => !!v || 'This field is required'],
      servicesRules: [v => !!v || 'This field is required'],
      percentageRules: [
        v => !!v || 'This field is required',
        v => /^\d+(\.\d+)?$/.test(v) || 'Number only',
        v =>
          (v && v.toString().split('.').length < 2) ||
          (v && v.toString().split('.')[1].length <= 2) ||
          'No more than 2 digits after the decimal point',
        v => (v && v >= 0) || 'Min should be above 0%',
        v => (v && v <= 100) || 'Max should not be above 100$'
      ],
      amountRules: [
        v => !!v || 'This field is required',
        v => /^\d+(\.\d+)?$/.test(v) || 'Number only',
        v =>
          (v && v.toString().split('.').length < 2) ||
          (v && v.toString().split('.')[1].length <= 2) ||
          'No more than 2 digits after the decimal point',
        v => (v && v >= 0) || 'Min should be above 0'
      ]
    }
  }),
  async created() {
    this.loading = true
    await this.getPlans()
    await this.getPricingModels()
    this.initializePlansCustom()
    this.loading = false
  },
  computed: {
    ...mapState({
      organizationCurrencyCode: ({ organization: { organization } }) =>
        organization.currency,
      organization_id: ({ organization: { organization } }) => organization.id,
      currencies: ({ countries: { currencies } }) => currencies,
      plans: ({ plans: { plans } }) => plans,
      services: ({ plans: { services } }) => services,
      pricingModels: ({ pricingModels: { pricingModels } }) => pricingModels
    }),
    currenciesPlans() {
      /* ALLOWS THE USER TO SELECT THE POSSIBLES CURRENCIES FOR THE PLAN */
      const pricing = this.findPricingModel(
        this.organizationPlanCreate.pricing_model_id
      )
      if (pricing) {
        const { currencies } = pricing

        return currencies
      }
      return []
    },
    /* SHOWS THE POSSIBLES PLANS DEPENDING OF THE PREVIOUS SELECTED CURRENCY */
    filteredPlans() {
      const { plans, organizationPlanCreate } = this
      return plans.filter(plan => {
        return (
          (plan.currency_id === organizationPlanCreate.currency_id &&
            plan.pricing_model_id ===
              organizationPlanCreate.pricing_model_id) ||
          !plan.currency_id
        )
      })
    },
    /* SHOWS THE POSSIBLES SERVICES DEPENDING OF THE PREVIOUS SELECTED CURRENCY */
    filteredServices() {
      const { services, organizationPlanCreate } = this
      return services.filter(service => {
        return service.currency_id === organizationPlanCreate.currency_id
      })
    },
    /* FINDS THE RELATED SERVICES OF THE SELECTED PLAN */
    defaultRelatedServices() {
      const { plans, organizationPlanCreate } = this

      const relatedPlanServices = plans.find(
        plan => plan.id === organizationPlanCreate.plan_id
      ).billingServices

      if (relatedPlanServices) {
        organizationPlanCreate.services = relatedPlanServices
        return relatedPlanServices
      }

      return null
    },
    showServicesAndResume() {
      return ![this.getPlanIdByName('no_plan')].includes(
        this.organizationPlanCreate.plan_id
      )
    },
    displaySummary() {
      return this.organizationPlanCreate.plan_id
    },
    formattedPlanSummary() {
      const { organizationPlanCreate, planDetail } = this

      return {
        plan: [
          {
            text: 'Nombre',
            value: planDetail.display_name
          },
          {
            text: 'Divisa',
            value: `${planDetail.currency.name} (${planDetail.currency.code})`
          },
          {
            text: 'Monto',
            value: planDetail.amount
              ? `${planDetail.currency.symbol} ${planDetail.amount}`
              : 'N/A'
          },
          {
            text: 'Porcentaje',
            value: planDetail.fee_percentage
              ? `${planDetail.fee_percentage}%`
              : 'N/A'
          }
        ],
        services: organizationPlanCreate.services.map(service => [
          {
            text: 'Servicio',
            value: service.display_name
          },
          {
            text: 'Costo unitario',
            value: `${planDetail.currency.symbol} ${service.cost}`
          }
        ])
      }
    }
  },
  methods: {
    ...mapActions({
      getPlans: 'plans/getPlans',
      getPricingModels: 'pricingModels/getPricingModels'
    }),
    initializePlansCustom() {
      this.plansCustom = [
        this.getPlanIdByName('monthly_license_USD'),
        this.getPlanIdByName('monthly_license_BRL'),
        this.getPlanIdByName('monthly_license_MXN'),
        this.getPlanIdByName('monthly_license_PEN'),
        this.getPlanIdByName('monthly_license_COP'),
        this.getPlanIdByName('monthly_license_EUR'),
        this.getPlanIdByName('lite_monthly_license_USD'),
        this.getPlanIdByName('lite_monthly_license_BRL'),
        this.getPlanIdByName('lite_monthly_license_MXN'),
        this.getPlanIdByName('lite_monthly_license_PEN'),
        this.getPlanIdByName('lite_monthly_license_COP'),
        this.getPlanIdByName('lite_monthly_license_EUR'),
        this.getPlanIdByName('essentials_monthly_license_USD'),
        this.getPlanIdByName('essentials_monthly_license_BRL'),
        this.getPlanIdByName('essentials_monthly_license_MXN'),
        this.getPlanIdByName('essentials_monthly_license_PEN'),
        this.getPlanIdByName('essentials_monthly_license_COP'),
        this.getPlanIdByName('essentials_monthly_license_EUR'),
        this.getPlanIdByName('professional_monthly_license_USD'),
        this.getPlanIdByName('professional_monthly_license_BRL'),
        this.getPlanIdByName('professional_monthly_license_MXN'),
        this.getPlanIdByName('professional_monthly_license_PEN'),
        this.getPlanIdByName('professional_monthly_license_COP'),
        this.getPlanIdByName('professional_monthly_license_EUR')
      ]
    },
    getPlan() {
      const plan = this.plans.find(plan => {
        return plan.id === this.organizationPlanCreate.plan_id
      })

      return plan
    },
    getCurrency() {
      const currency = this.currencies.find(
        curr => curr.id === this.organizationPlanCreate.currency_id
      )
      return currency
    },
    updatePricingModel() {
      // Reset organizationPlan values when pricing model changes
      this.organizationPlanCreate.currency_id = null
      this.organizationPlanCreate.plan_id = null
      this.organizationPlanCreate.services = []
      this.organizationPlanCreate.fee_percentage = null
      this.organizationPlanCreate.amount = null
    },
    updateCurrency() {
      // Reset organizationPlan values when currency changes
      this.organizationPlanCreate.plan_id = null
      this.organizationPlanCreate.services = []
      this.organizationPlanCreate.fee_percentage = null
      this.organizationPlanCreate.amount = null

      // Set currency in the inputs
      const currency = this.getCurrency()
      this.currencySelected = currency ? currency.code : null
    },
    updatePlan() {
      this.planDetail = this.getPlan()

      // Reset organizationPlan values when plan changes
      this.organizationPlanCreate.services = []
      this.organizationPlanCreate.fee_percentage = null
      this.organizationPlanCreate.amount = null
    },
    updateServices(selectedServices) {
      const { organizationPlanCreate } = this

      // Creamos un Set para almacenar los tipos ya vistos
      const seenTypes = new Set()

      // Filtramos los servicios seleccionados
      organizationPlanCreate.services = selectedServices.filter(service => {
        if (seenTypes.has(service.type)) {
          // Si el tipo ya está en el Set, no incluimos este servicio
          return false
        } else {
          // Si es un nuevo tipo, lo añadimos al Set y mantenemos el servicio
          seenTypes.add(service.type)
          return true
        }
      })
    },
    isServiceTypeSelected(item) {
      return this.organizationPlanCreate.services.some(
        service => service.type === item.type
      )
    },
    setPercentage(selectedPlan) {
      let CUSTOM_PERCENTAGE =
        this.organizationPlanCreate.fee_percentage && selectedPlan.custom_fee

      if (CUSTOM_PERCENTAGE) {
        this.organizationPlanCreate.fee_percentage = this.organizationPlanCreate.fee_percentage.replace(
          ',',
          '.'
        )
      } else if (selectedPlan) {
        this.organizationPlanCreate.fee_percentage = selectedPlan.fee_percentage.replace(
          ',',
          '.'
        )
      }
    },
    setAmount(selectedPlan) {
      const RELATED_PLAN_AMOUNT =
        !this.organizationPlanCreate.amount && selectedPlan
      if (RELATED_PLAN_AMOUNT) {
        this.organizationPlanCreate.amount = selectedPlan.amount
      }
    },
    setNoPlan(selectedPlan) {
      const VALIDATE_NO_PLAN =
        this.organizationPlanCreate.plan_id === this.getPlanIdByName('no_plan')
      if (VALIDATE_NO_PLAN) {
        // Relate automation no plan
        this.organizationPlanCreate.services = []
      }
    },
    findPricingModel(pricing_model_id) {
      return this.pricingModels.find(pricing => pricing.id === pricing_model_id)
    },
    planHasServicesRelated() {
      const plan = this.getPlan()
      return plan.billingServices.length > 0
    },
    planHasCustomAutomation() {
      const plan = this.getPlan()
      const { custom_automation } = plan
      return custom_automation
    },
    planHasCustomAmount() {
      const plan = this.getPlan()
      const { custom_amount } = plan
      return custom_amount
    },
    planHasCustomFee() {
      const plan = this.getPlan()
      const { custom_fee } = plan
      return custom_fee
    },
    validate() {
      this.$refs.form.validate()
    },
    reset() {
      this.$refs.form.reset()
    },
    resetValidation() {
      this.$refs.form.resetValidation()
    },
    createOrganizationPlan() {
      if (this.$refs.form.validate()) {
        // Get the selected plan based on plan_id
        const selectedPlan = this.plans.find(
          plan => plan.id === this.organizationPlanCreate.plan_id
        )

        // SET FEE PERCENTAGE - CUSTOM OR PLAN RELATED
        this.setPercentage(selectedPlan)
        // SET AMOUNT - CUSTOM OR PLAN RELATED
        this.setAmount(selectedPlan)
        // NO PLAN
        this.setNoPlan(selectedPlan)

        this.$emit('proceed', this.organizationPlanCreate)
      }
    }
  }
}
</script>
